import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import {
  addSessionConfig,
  removeSessionConfig,
  setSelectedSessionConfig,
  updateSessionConfig,
} from 'bundles/widget-simulator/actions/WidgetSimulatorActions';
import OptionsFormGroup from 'bundles/widget-simulator/components/tools/OptionsFormGroup';
import SessionConfigurationEditorModal from 'bundles/widget-simulator/components/tools/SessionConfigurationEditorModal';
import ToolSection from 'bundles/widget-simulator/components/tools/ToolSection';
import type WidgetSimulatorStoreClass from 'bundles/widget-simulator/stores/WidgetSimulatorStore';
import type { Option, OptionProps } from 'bundles/widget-simulator/types';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/SessionConfigurationEditor';

export type Props = {
  options: Array<Option>;
  selectedOption: Option | undefined;
};

export class SessionConfigurationEditor extends React.Component<Props, {}> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof SessionConfigurationEditor.contextTypes>;

  state = {
    showAddModal: false,
    showEditModal: false,
  };

  handleAddButtonClick = () => {
    this.setState({ showAddModal: true });
  };

  handleEditButtonClick = () => {
    this.setState({ showEditModal: true });
  };

  handleRemoveButtonClick = () => {
    const { executeAction } = this.context;
    const { options, selectedOption } = this.props;
    const selectedIndex: number = options.findIndex(
      (option: Option) => selectedOption && option.id === selectedOption.id
    );

    if (selectedIndex === -1) {
      return;
    }

    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert,
    if (confirm('Are you sure you want to delete this session configuration? This cannot be undone.')) {
      executeAction(removeSessionConfig, { index: selectedIndex });
    }
  };

  handleAddSaveButtonClick = (newProps: OptionProps | undefined) => {
    const { executeAction } = this.context;

    if (!newProps) {
      return;
    }

    executeAction(addSessionConfig, newProps);
    this.setState({ showAddModal: false });
  };

  handleEditSaveButtonClick = (newProps: OptionProps | undefined) => {
    const { executeAction } = this.context;
    const { selectedOption } = this.props;

    if (!selectedOption || !newProps) {
      return;
    }

    executeAction(updateSessionConfig, {
      id: selectedOption.id,
      newProps,
    });
    this.setState({ showEditModal: false });
  };

  handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { executeAction } = this.context;
    const { options } = this.props;
    const selectedId = event.target.value;
    const selectedIndex = options.findIndex((option) => option.id === selectedId);

    executeAction(setSelectedSessionConfig, { selectedIndex });
  };

  closeAddModal = () => {
    this.setState({ showAddModal: false });
  };

  closeEditModal = () => {
    this.setState({ showEditModal: false });
  };

  renderOptions() {
    const { options } = this.props;

    if (!options) {
      return <option>{_t('Loading...')}</option>;
    } else if (options.length === 0) {
      return <option>{_t('Add a Session Configuration...')}</option>;
    } else {
      return options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ));
    }
  }

  renderHelp() {
    // TODO: Move this to its own component.
    return (
      <div>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                A session configuration is a JSON value you have access to in a plugin
                via <code>GET_SESSION_CONFIGURATION</code>.
              `
            )}
          />
        </p>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                On the Coursera platform, when you create a new instance of a plugin you define a "session provider",
                which creates session configurations from a template.
              `
            )}
          />
        </p>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                Here you have access only to static values and the macros you define in your manifest.
              `
            )}
          />
        </p>
      </div>
    );
  }

  render() {
    const { options, selectedOption } = this.props;
    const { showAddModal, showEditModal } = this.state;

    return (
      <div>
        <ToolSection
          className="rc-SessionConfigurationEditor"
          name={_t('Session Configuration')}
          help={this.renderHelp()}
        >
          <p className="hint caption-text">{_t('Create a configuration for this plugin instance.')}</p>

          <OptionsFormGroup
            label={_t('Instance')}
            options={options}
            selectedOption={selectedOption}
            onAddButtonClick={this.handleAddButtonClick}
            onEditButtonClick={this.handleEditButtonClick}
            onRemoveButtonClick={this.handleRemoveButtonClick}
          >
            <select className="control" value={selectedOption && selectedOption.id} onChange={this.handleSelectChange}>
              {this.renderOptions()}
            </select>
          </OptionsFormGroup>
        </ToolSection>

        {showAddModal && (
          <SessionConfigurationEditorModal
            name={_t('Add Session Configuration Modal')}
            title={_t('Add Session Configuration')}
            onClose={this.closeAddModal}
            onSaveButtonClick={this.handleAddSaveButtonClick}
          />
        )}

        {showEditModal && (
          <SessionConfigurationEditorModal
            name={_t('Edit Session Configuration Modal')}
            title={_t('Edit Session Configuration')}
            session={selectedOption}
            onClose={this.closeEditModal}
            onSaveButtonClick={this.handleEditSaveButtonClick}
          />
        )}
      </div>
    );
  }
}

type Stores = {
  WidgetSimulatorStore: typeof WidgetSimulatorStoreClass;
};

export default compose<Props, {}>(
  connectToStores<Props, {}, Stores>(['WidgetSimulatorStore'], ({ WidgetSimulatorStore }) => ({
    options: WidgetSimulatorStore.getSessionConfigs(),
    selectedOption: WidgetSimulatorStore.getSelectedSessionConfig(),
  }))
)(SessionConfigurationEditor);
